import { Button } from '@casavo/base-ui'
import cn from 'classnames'
import { Form, Formik } from 'formik'
import useTranslation from 'next-translate/useTranslation'
import router from 'next/router'
import React, { ComponentType, useState } from 'react'

import Icon from '@/components/Icon'
import Arrow from '@/components/Icon/Arrow'
import { ProgressBar } from '@/components/ProgressBar'
import { HouseType, HouseTypeValidation } from '@/containers/SuggestedProperties/Onboarding/Wizard/steps/HouseType'
import { MaxBudget, MaxBudgetValidation } from '@/containers/SuggestedProperties/Onboarding/Wizard/steps/MaxBudget'
import {
  PreferredZones,
  PreferredZonesValidation,
} from '@/containers/SuggestedProperties/Onboarding/Wizard/steps/PreferredZones'
import { useDevice } from '@/context/Device'
import { suggestedOnboardingPath } from '@/i18n-routes'
import { GeoFilter } from '@/types/filter'
import { buildRelativeLocalizedLink } from '@/utils/navigation'
import { GTMEventCategory } from '@/utils/tracking/eventCategory'
import { handleClick } from '@/utils/tracking/eventsHandlers'

import {
  buyerInterestFlowFooter,
  buyerInterestFlowFooterContent,
  buyerInterestFlowFooterHidden,
  wrapper,
} from './styles.css'

interface Step {
  component: ComponentType
  gtmEventLabel?: 'SelectZone' | 'SelectRooms' | 'Budget'
  options: { next: boolean; prev: boolean }
  validation?: any
}

export type WizardValues = {
  maxBudget: number
  preferredZones: GeoFilter[]
  roomsMin: string
}

function createSteps(): Step[] {
  return [
    {
      component: PreferredZones,
      gtmEventLabel: 'SelectZone',
      options: { next: true, prev: true },
      validation: PreferredZonesValidation(),
    },
    {
      component: HouseType,
      gtmEventLabel: 'SelectRooms',
      options: { next: true, prev: true },
      validation: HouseTypeValidation(),
    },
    {
      component: MaxBudget,
      gtmEventLabel: 'Budget',
      options: { next: false, prev: true },
      validation: MaxBudgetValidation(),
    },
  ]
}

const initialValues: WizardValues = {
  maxBudget: null,
  preferredZones: null,
  roomsMin: null,
}

type Props = {
  handleSubmit: (values: WizardValues) => void
}

export const Wizard: React.FC<Props> = ({ handleSubmit }) => {
  const { lang, t } = useTranslation()
  const [currentStep, setCurrentStep] = useState<number>(0)
  const device = useDevice()
  const steps = createSteps()
  const step = steps[currentStep]
  const shouldHideFooter = device === 'mobile' && !step.options.prev && !step.options.next

  const handlePrevClick = () => {
    handleClick(GTMEventCategory.MainSearchFlow, `${step.gtmEventLabel}_back`)
    if (currentStep === 0) {
      router.push(buildRelativeLocalizedLink(suggestedOnboardingPath, lang))
      return
    }
    setCurrentStep((step) => step - 1)
  }

  return (
    <div className={wrapper} data-testid="wizard-component">
      <Formik initialValues={initialValues} validationSchema={step.validation} onSubmit={(data) => handleSubmit(data)}>
        {({ isValid }) => {
          return (
            <>
              <Form>
                <step.component />
                <div className={buyerInterestFlowFooter}>
                  <ProgressBar currentStep={currentStep + 1} totalSteps={steps.length} />
                  <div
                    className={cn(buyerInterestFlowFooterContent, shouldHideFooter && buyerInterestFlowFooterHidden)}
                  >
                    {step.options.prev && (
                      <Button
                        ariaLabel="Back"
                        Icon={() => <Arrow />}
                        styleVariant="tertiary"
                        onClick={handlePrevClick}
                      />
                    )}

                    {step.options.next && (
                      <Button
                        iconRight
                        data-testid="next-button"
                        disabled={!isValid}
                        Icon={() => <Arrow direction="right" />}
                        label={t('common:CTA_Next')}
                        onClick={() => {
                          step.gtmEventLabel &&
                            handleClick(GTMEventCategory.MainSearchFlow, `${step.gtmEventLabel}_next`)
                          setCurrentStep((step) => step + 1)
                        }}
                      />
                    )}

                    {currentStep === steps.length - 1 && (
                      <Button
                        data-testid="submit-button"
                        disabled={!isValid}
                        Icon={() => <Icon.Check />}
                        label={t('suggested:BudgetQuestion_Submit')}
                        type="submit"
                      />
                    )}
                  </div>
                </div>
              </Form>
            </>
          )
        }}
      </Formik>
    </div>
  )
}
